// Single source of truth for KnowMeno subscription plans.
// Price IDs are LIVE mode (overridable via env vars).
// Plus tier: monthly $9.99, annual $79 (quarterly removed 2026-06-06).

const env = (k: string) =>
  typeof process !== "undefined" && process.env ? process.env[k] : undefined;

export type PlanId = "monthly" | "annual";

export interface Plan {
  id: PlanId;
  name: string;
  priceId: string;
  productId: string;
  amountCents: number;
  displayPrice: string;
  cadence: string; // shown under price
  perMonthEquivalent?: string; // e.g. "$6.58/mo"
  badge?: string; // e.g. "Most popular"
  savings?: string; // e.g. "Save 34%"
  popular?: boolean;
}

export const PLANS: Record<PlanId, Plan> = {
  monthly: {
    id: "monthly",
    name: "Monthly",
    priceId: env("STRIPE_PRICE_MONTHLY") ?? "price_1TfQWUGiugjm6UTnYwfVfkif",
    productId: "",
    amountCents: 999,
    displayPrice: "$9.99",
    cadence: "per month",
  },
  annual: {
    id: "annual",
    name: "Annual",
    priceId: env("STRIPE_PRICE_ANNUAL") ?? "price_1TfQYbGiugjm6UTnCZGtaBol",
    productId: "",
    amountCents: 7900,
    displayPrice: "$79",
    cadence: "per year",
    perMonthEquivalent: "$6.58/mo",
    badge: "Best value",
    savings: "Save 34%",
    popular: true,
  },
};

export const PLAN_ORDER: PlanId[] = ["monthly", "annual"];

// Reverse lookup: given a Stripe price ID, identify our cadence tier.
export function tierFromPriceId(priceId: string | null | undefined): PlanId | null {
  if (!priceId) return null;
  for (const id of PLAN_ORDER) {
    if (PLANS[id].priceId === priceId) return id;
  }
  return null;
}

// Plan tier in the freemium model (separate from cadence/PlanId).
export type PlanTier = "free" | "plus" | "premium";

// Resolve plan tier from a Stripe price lookup_key. The convention is:
//   plus_monthly / plus_annual → 'plus'
//   premium_*                  → 'premium'
//   anything else              → 'free'
export function tierFromLookupKey(lookupKey: string | null | undefined): PlanTier {
  if (!lookupKey) return "free";
  const key = lookupKey.toLowerCase();
  if (key === "plus_monthly" || key === "plus_annual") {
    return "plus";
  }
  if (key.startsWith("premium_")) return "premium";
  return "free";
}

// Single tier resolver used by the webhook + sync path: prefer lookup_key,
// fall back to price ID (all PLANS prices map to 'plus' today).
export function resolveSubscriptionTier(
  lookupKey: string | null | undefined,
  priceId: string | null | undefined,
): PlanTier {
  const fromLookup = tierFromLookupKey(lookupKey);
  if (fromLookup !== "free") return fromLookup;
  return tierFromPriceId(priceId) ? "plus" : "free";
}
